import { useState, useMemo, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Plus,
  Search,
  Filter,
  Download,
  Loader2,
  List,
  FolderOpen,
  ArchiveRestore,
} from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import {
  useAssortmentPlans,
  useDeletePlan,
  useCopyPlan,
} from '@/hooks/useAssortmentPlans'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { useDebounce } from '@/hooks/useDebounce'
import { useExportPlans } from '@/hooks/useExportPlans'
import { OwnerFilterCombobox } from '@/components/common/OwnerFilterCombobox'
import { useSeasons } from '@/hooks/useSeasons'
import { mapStatusToApi } from '@/api/assortmentPlans'
import { Combobox } from '@/components/ui/combobox'
import { HierarchyFilter } from '@/components/AssortmentPlansGrid/HierarchyFilter'
import { PlanWizard } from '@/components/PlanWizard/PlanWizard'
import { usePlanWizardStore } from '@/stores/usePlanWizardStore'
import { ConfirmationDialog } from '@/components/common/ConfirmationDialog'
import { Pagination } from '@/components/common/Pagination'
import { usePersistedPageSize } from '@/hooks/usePersistedPageSize'
import { useElementHeight } from '@/hooks/useElementHeight'
import { TABLE_PAGE_SIZE_KEYS } from '@/stores/useTablePageSizeStore'
import {
  EditablePlanNameCell,
  ActiveVersionCell,
  TextCell,
  ActionsCell,
  StatusBadgeCell,
} from '@/components/AssortmentPlansGrid/cellRenderers'
import { FolderBrowseView } from '@/components/FolderBrowse/FolderBrowseView'
import { useBrowseStore } from '@/stores/useBrowseStore'
import { cn } from '@/utils/cn'
import type { AssortmentPlan } from '@/types'

// Register AG Grid modules
ModuleRegistry.registerModules([AllCommunityModule])

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Status' },
  { value: 'Draft', label: 'Draft' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Sent Back', label: 'Sent Back' },
]

export default function AssortmentPlans() {
  const navigate = useNavigate()
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [ownerFilter, setOwnerFilter] = useState<string | null>(null)
  const [hierarchyFilter, setHierarchyFilter] = useState<{
    departmentId: string | null
    classId: string | null
    subclassId: string | null
  }>({ departmentId: null, classId: null, subclassId: null })
  const [seasonFilter, setSeasonFilter] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = usePersistedPageSize(
    TABLE_PAGE_SIZE_KEYS.PLANS_LIST,
    20
  )
  const { openWizard } = usePlanWizardStore()
  const { viewMode, setViewMode } = useBrowseStore()
  const { ref: filterBarRef, height: filterBarHeight } =
    useElementHeight<HTMLDivElement>()

  // Seasons for season filter
  const { data: seasons, isLoading: seasonsLoading } = useSeasons()
  const seasonOptions = useMemo(
    () =>
      (seasons ?? []).map(s => ({
        value: s.id,
        label: s.name,
      })),
    [seasons]
  )

  // Compute the most specific hierarchy ID for filtering
  const hierarchyId =
    hierarchyFilter.subclassId ??
    hierarchyFilter.classId ??
    hierarchyFilter.departmentId

  // Modal state for actions
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedPlan, setSelectedPlan] = useState<AssortmentPlan | null>(null)

  // Mutations
  const deleteMutation = useDeletePlan()
  const copyMutation = useCopyPlan()

  // Export
  const { exportPlans, isProcessing: isExporting } = useExportPlans()

  const handleCopyClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setCopyDialogOpen(true)
  }, [])

  const handleDeleteClick = useCallback((plan: AssortmentPlan) => {
    setSelectedPlan(plan)
    setDeleteDialogOpen(true)
  }, [])

  const handleConfirmCopy = useCallback(() => {
    if (selectedPlan && selectedPlan.active_version_id) {
      copyMutation.mutate(
        {
          name: `${selectedPlan.name} (Copy)`,
          sourceVersionId: selectedPlan.active_version_id,
          hierarchyId: selectedPlan.hierarchy_id,
        },
        {
          onSettled: () => {
            setCopyDialogOpen(false)
            setSelectedPlan(null)
          },
        }
      )
    }
  }, [selectedPlan, copyMutation])

  const handleConfirmDelete = useCallback(() => {
    if (selectedPlan) {
      deleteMutation.mutate(selectedPlan.id, {
        onSettled: () => {
          setDeleteDialogOpen(false)
          setSelectedPlan(null)
        },
      })
    }
  }, [selectedPlan, deleteMutation])

  // Column definitions for AG Grid - responsive with flex sizing
  const columnDefs: ColDef<AssortmentPlan>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 180,
        cellRenderer: EditablePlanNameCell,
      },
      {
        field: 'hierarchy',
        headerName: 'Hierarchy',
        flex: 3,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'versions_count',
        headerName: 'Version',
        flex: 1,
        minWidth: 100,
        maxWidth: 200,
        cellRenderer: ActiveVersionCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1,
        minWidth: 100,
        cellRenderer: TextCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 1,
        minWidth: 100,
        maxWidth: 130,
        cellRenderer: StatusBadgeCell,
      },
      {
        headerName: 'Actions',
        flex: 1,
        minWidth: 90,
        maxWidth: 120,
        cellClass: 'ag-right-aligned-cell',
        headerClass: 'ag-right-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onCopy: handleCopyClick,
          onDelete: handleDeleteClick,
        },
      },
    ],
    [handleCopyClick, handleDeleteClick]
  )

  // Handle dynamic resizing when container size changes
  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  // Debounce search input to avoid excessive API calls
  const debouncedSearch = useDebounce(searchInput, 300)

  // Export handler
  const handleExport = useCallback(() => {
    exportPlans({
      q: debouncedSearch || undefined,
      status: mapStatusToApi(statusFilter),
    })
  }, [exportPlans, debouncedSearch, statusFilter])

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1)
  }, [
    debouncedSearch,
    statusFilter,
    ownerFilter,
    hierarchyId,
    seasonFilter,
    pageSize,
  ])

  // Build filters object for API call
  const filters = useMemo(
    () => ({
      search: debouncedSearch,
      status: statusFilter,
      owner_id: ownerFilter ?? undefined,
      hierarchy_id: hierarchyId ?? undefined,
      season_id: seasonFilter ?? undefined,
      page,
      pageSize,
    }),
    [
      debouncedSearch,
      statusFilter,
      ownerFilter,
      hierarchyId,
      seasonFilter,
      page,
      pageSize,
    ]
  )

  const { data, isLoading } = useAssortmentPlans(filters)

  const plans = data?.plans ?? []

  const totalPages = data?.totalPages ?? 1

  return (
    <>
      <PlanWizard />

      {/* Copy Confirmation Dialog */}
      <ConfirmationDialog
        open={copyDialogOpen}
        onOpenChange={setCopyDialogOpen}
        title="Copy plan"
        description={
          selectedPlan?.active_version_id
            ? `Are you sure you want to copy "${selectedPlan?.name ?? ''}"? A duplicate will be created.`
            : `Cannot copy "${selectedPlan?.name ?? ''}" because it has no active version.`
        }
        confirmText="Copy"
        cancelText="Cancel"
        onConfirm={handleConfirmCopy}
        isLoading={copyMutation.isPending}
        confirmDisabled={!selectedPlan?.active_version_id}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete plan"
        description={`Are you sure you want to delete "${selectedPlan?.name ?? ''}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deleteMutation.isPending}
      />

      <div
        className="flex flex-col gap-6 p-6"
        style={
          {
            '--sticky-toolbar-offset': `${filterBarHeight}px`,
          } as React.CSSProperties
        }
      >
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">
              Assortment Plans
            </h1>
            <p className="text-base text-foreground mt-1">
              Manage and create category assortment plans
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center rounded-md border border-border">
              <button
                type="button"
                aria-label="Folder view"
                aria-pressed={viewMode === 'folder'}
                onClick={() => setViewMode('folder')}
                className={cn(
                  'p-2 rounded-l-md cursor-pointer transition-colors',
                  viewMode === 'folder'
                    ? 'bg-[#FEF2F3] text-[#CF1F2E]'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                <FolderOpen className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="List view"
                aria-pressed={viewMode === 'list'}
                onClick={() => setViewMode('list')}
                className={cn(
                  'p-2 rounded-r-md cursor-pointer transition-colors',
                  viewMode === 'list'
                    ? 'bg-[#FEF2F3] text-[#CF1F2E]'
                    : 'text-muted-foreground hover:bg-muted/50'
                )}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    aria-label="Deleted plans"
                    onClick={() => navigate('/plans/deleted')}
                  >
                    <ArchiveRestore className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Deleted plans</TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <Button className="gap-2" onClick={openWizard}>
              <Plus className="h-4 w-4" />
              Create New Plan
            </Button>
          </div>
        </div>

        {viewMode === 'list' ? (
          <>
            {/* Filter Bar */}
            <div
              ref={filterBarRef}
              className="sticky top-0 z-20 flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              {/* Search Input */}
              <div className="relative flex-1 max-w-[352px]">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search plans..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-9 bg-muted/50"
                />
              </div>

              {/* Filter Icon */}
              <Filter className="h-6 w-6 text-muted-foreground" />

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Owner Filter */}
              <OwnerFilterCombobox
                value={ownerFilter}
                onValueChange={setOwnerFilter}
              />

              {/* Hierarchy Filter */}
              <HierarchyFilter
                value={hierarchyFilter}
                onValueChange={setHierarchyFilter}
              />

              {/* Season Filter */}
              <Combobox
                options={seasonOptions}
                value={seasonFilter}
                onValueChange={setSeasonFilter}
                placeholder="All Seasons"
                isLoading={seasonsLoading}
                className="w-[180px]"
              />

              {/* Separator */}
              <Separator orientation="vertical" className="h-10" />

              <Button
                variant="outline"
                className="gap-2"
                onClick={handleExport}
                disabled={isExporting}
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Export
              </Button>
            </div>

            {/* AG Grid Table */}
            <div className="w-full">
              <div className="border border-[#e7e7e7] bg-white">
                <div className="ag-theme-michaels plans-list-grid w-full">
                  <AgGridReact<AssortmentPlan>
                    rowData={plans}
                    columnDefs={columnDefs}
                    theme="legacy"
                    loading={isLoading}
                    domLayout="autoHeight"
                    rowHeight={68}
                    headerHeight={48}
                    autoSizeStrategy={{ type: 'fitGridWidth' }}
                    onGridSizeChanged={onGridSizeChanged}
                    overlayLoadingTemplate="<span>Loading...</span>"
                    overlayNoRowsTemplate="<span>No plans found</span>"
                    suppressCellFocus={true}
                    suppressRowHoverHighlight={false}
                    suppressColumnVirtualisation={true}
                    suppressMovableColumns={true}
                  />
                </div>

                {/* Pagination */}
                <Pagination
                  page={page}
                  pageSize={pageSize}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  onPageSizeChange={setPageSize}
                />
              </div>
            </div>
          </>
        ) : (
          <FolderBrowseView />
        )}
      </div>
    </>
  )
}
